<template>
    <div>
        <NuxtLoadingIndicator />
        <div v-if="fontHtml" v-html="fontHtml" />

        <NuxtLayout :name="layoutType">
            <slot />
        </NuxtLayout>

        <AlertBar v-if="alert && !isMicrosite" />
    </div>
</template>

<script setup>
    const globalStore = useGlobalStore()
    const { apiUrl, craftUrl } = useGraphQL()

    const {
        digistormSettings,
        globalSets,
        themeCss,
        fontWeightCss,
        getGlobalSetField,
        pageType,
        getGlobalTheme,
        locale,
    } = storeToRefs(globalStore)

    const { isMicrosite } = storeToRefs(useMicrositeStore())

    await globalStore.loadSite()

    provide('websiteTier', digistormSettings.value?.websiteTier)
    provide('animationGlobal', digistormSettings.value?.animationGlobal)
    provide('apiUrl', apiUrl)
    provide('craftUrl', craftUrl)
    provide('locale', locale)
    provide('globalSets', globalSets)

    const alert = computed(() => {
        return find(globalSets.value, { name: 'Alert bar' })
    })

    const { roundness, btnRoundness } = useRoundness(digistormSettings)

    const addFavicon = () => {
        const logos = find(globalSets.value, { name: 'Logos'})
        const favicon = get(logos, 'logos_BrowserIcon[0].url')
        if (!favicon) {
            return
        }
        useHead({
            htmlAttrs: {
                lang: 'en'
            },
            link: [
                {
                    rel: 'icon',
                    type: 'image/x-icon',
                    href: favicon
                }
            ]
        })
    }

    const addGlobalCode = () => {
        const globalCode = find(globalSets.value, { __typename: 'ds_GlobalCode_GlobalSet' })
        const headCode = get(globalCode, 'globalCode_HeadCode')
        const bodyCode = get(globalCode, 'globalCode_BodyCode')

        if (headCode) {
            const headElement = document.createElement('div')
            headElement.id = 'global-head-code'
            headElement.innerHTML = headCode
            document.head.appendChild(headElement)

            const scripts = []

            // Extract all scripts from the head code
            const headScripts = headElement.getElementsByTagName('script')
            for (const script of headScripts) {
                if (script.src) {
                    scripts.push({
                        src: script.src ?? '',
                        type: script.type || 'text/javascript',
                    })
                } else {
                    scripts.push({
                        innerHTML: script.textContent ?? '',
                        type: script.type || 'text/javascript',
                    })
                }

            }

            useHead({
                script: scripts
            })
        }

        if (bodyCode) {
            const bodyElement = document.createElement('div')
            bodyElement.id = 'global-body-code'
            bodyElement.innerHTML = bodyCode
            document.body.appendChild(bodyElement)

            // Extract and execute any script tags in the body code
            const bodyScripts = bodyElement.getElementsByTagName('script')
            for (const script of bodyScripts) {
                const newScript = document.createElement('script')
                newScript.type = script.type || 'text/javascript'
                if (script.src) {
                    newScript.src = script.src
                } else {
                    newScript.textContent = script.textContent
                }
                document.body.appendChild(newScript)
            }
        }

    }

    addFavicon()

    onMounted(() => {
        addGlobalCode()
    })

    const layoutType = computed(() => {
        if (isMicrosite.value) {
            return 'microsite'
        }

        const secondaryType = getGlobalSetField.value('ds_Navigation_GlobalSet', 'secondaryNav_Type')

        if (secondaryType === 'off') {
            return 'top-bar'
        }

        // disable sidebar for internal pages
        if (pageType.value !== 'home') {
            return 'top-bar'
        }

        return secondaryType || 'top-bar'
    })


    const fontHtml = computed(() => {
        return `
${digistormSettings.value?.fontHtml}
<style>
    :root {
        --font-brand-subheading: var(--typography-font-family-heading);
        --font-brand-content: var(--typography-font-family-body);
        --font-brand-heading: var(--typography-font-family-heading);
        --font-brand-lead: var(--typography-font-family-lead);
        --font-ux: var(--typography-font-family-body);
    }
</style>`
    })

    each(themeCss.value, (styles) => {
        useHead({
            style: [
                {
                    innerHTML: styles.css,
                    id: `site-theme-${styles.id}`,
                },
            ]
        })
    })

    useHead({
        htmlAttrs: {
            'data-theme': getGlobalTheme.value,
            'data-mode': 'base',
            'data-radius': roundness.value,
            'data-button-radius': btnRoundness.value,
            class: 'surface-primary font-ux'
        },
        style: [
            {
                innerHTML: fontWeightCss.value,
                id: 'site-font-weights',
            },
        ]
    })

</script>
